import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EntityUsageCard } from '../components/EntityUsageCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { SensitiveCredential } from '../components/ui/SensitiveCredential';
import { Modal } from '../components/ui/Modal';
import { TenantForm } from '../components/forms/TenantForm';
import { PinCell } from '../components/PinCell';
import { CallRecord } from '../types';
import { formatDateTime, formatDuration } from '../utils/formatters';
import { getTenantById, updateTenant } from '../api/tenants';
import { getUnits } from '../api/units';
import { getProperties } from '../api/properties';
import { getPDKHolder } from '../api/pdkApi';
import { apiList, apiGet, apiRequestGet, apiUpdate } from '../api/apiClient';
import { generateActivationCode } from '../api/intercomConfig';
import { CallHistory } from './CallHistory';
import { refreshAllCounters } from '../utils/refreshCounters';
import { formatDeviceType } from '../utils/presentation';
import { mergeVisitorPassRecords } from '../utils/visitorPassRecords';
import { apiOrigin } from '../utils/surface';
import { useAuth } from '../hooks/useAuth';

const isDeliveryPin = (record: any) =>
  String(record?.pin_type ?? record?.kind ?? record?.access_type ?? '').toLowerCase() === 'delivery';

const wasCreatedByPerson = (record: any, personId: string) => [
  record?.granted_by_tenant_id,
  record?.created_by_tenant_id,
  record?.created_by_person_id,
  record?.creator_person_id,
  record?.tenant_id,
  record?.person_id,
].some((value) => value != null && String(value) === String(personId));

export const TenantDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const canManageFaceReference = ['super_admin', 'admin', 'property_manager', 'property', 'manager', 'customer', 'customer_admin', 'dealer', 'integrator']
    .includes(String(user?.role || '').toLowerCase());
  const [editModal, setEditModal] = useState(false);
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pdkData, setPdkData] = useState<any>(null);
  const [pdkCached, setPdkCached] = useState<any>(null);
  const [pdkLoading, setPdkLoading] = useState(false);
  const [pdkError, setPdkError] = useState('');
  const [pdkLive, setPdkLive] = useState(false);
  const [pdkTab, setPdkTab] = useState<'overview' | 'groups' | 'custom'>('overview');
  const [detailTab, setDetailTab] = useState('overview');
  const [tenantProperties, setTenantProperties] = useState<any[]>([]);
  const [allUnits, setAllUnits] = useState<any[]>([]);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [accessibleDoors, setAccessibleDoors] = useState<any[]>([]);
  // Per-property door-access state keyed by property_id → {grants, hides}.
  // Sourced from GET /people/:id/property-doors which also gives the
  // baseline set so we can compute the effective Mobile Access toggle.
  type PropDoorAccess = {
    grants: Set<string>;
    hides: Set<string>;
    baseline: Set<string>;
    nameByKey: Map<string, string>;
  };
  const [doorAccessByProp, setDoorAccessByProp] = useState<Record<string, PropDoorAccess>>({});
  // Snapshot taken at load time so the Save button knows which properties
  // have unsaved toggle changes. Toggling no longer saves immediately —
  // a Save button at the bottom of the Doors with Access card persists
  // every dirty property in one batch.
  const [doorAccessOrigByProp, setDoorAccessOrigByProp] = useState<Record<string, PropDoorAccess>>({});
  const [doorAccessSaving, setDoorAccessSaving] = useState<boolean>(false);
  const [guestsCreated, setGuestsCreated] = useState<any[]>([]);
  const [deliveriesCreated, setDeliveriesCreated] = useState<any[]>([]);
  const [permSaving, setPermSaving] = useState(false);
  const [activationCode, setActivationCode] = useState<string | null>(null);
  const [activationSending, setActivationSending] = useState(false);
  const [activationError, setActivationError] = useState('');
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [callSchedules, setCallSchedules] = useState<any[]>([]);
  const [callSchedulesUnavailable, setCallSchedulesUnavailable] = useState(false);
  const [subtenants, setSubtenants] = useState<any[]>([]);
  const [addPropertyModal, setAddPropertyModal] = useState(false);
  const [addCustomerModal, setAddCustomerModal] = useState(false);
  const [addPropertySelected, setAddPropertySelected] = useState('');
  const [addUnitSelected, setAddUnitSelected] = useState('');
  const [unitsForSelectedProperty, setUnitsForSelectedProperty] = useState<any[]>([]);
  const [addCustomerSelected, setAddCustomerSelected] = useState('');
  const [addingProperty, setAddingProperty] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [lastAddResult, setLastAddResult] = useState<{code?: string; property_id?: string} | null>(null);
  const [facePhotoPropertyId, setFacePhotoPropertyId] = useState('');
  const [facePhotoPreview, setFacePhotoPreview] = useState('');
  const [facePhotoConsent, setFacePhotoConsent] = useState(false);
  const [faceReferencePhoto, setFaceReferencePhoto] = useState('');
  const [facePhotoBusy, setFacePhotoBusy] = useState(false);
  const [facePhotoError, setFacePhotoError] = useState('');

  const facePhotoAuth = () => {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` };
  };

  const prepareFacePhoto = async (file?: File) => {
    setFacePhotoError(''); setFacePhotoPreview(''); setFacePhotoConsent(false);
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setFacePhotoError('Choose a JPEG, PNG, or WebP image under 5 MB.'); return;
    }
    const source = URL.createObjectURL(file);
    try {
      const img = new Image(); img.src = source;
      await img.decode();
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 720 / Math.max(img.naturalWidth, img.naturalHeight));
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Image processing is unavailable');
      context.drawImage(img, 0, 0, canvas.width, canvas.height);
      setFacePhotoPreview(canvas.toDataURL('image/jpeg', 0.8));
    } catch { setFacePhotoError('The selected image could not be processed.'); }
    finally { URL.revokeObjectURL(source); }
  };

  const loadFaceReference = async (personId: string, propertyId: string) => {
    setFaceReferencePhoto('');
    if (!personId || !propertyId) return;
    const response = await fetch(`${apiOrigin()}/face/reference/${encodeURIComponent(personId)}?property_id=${encodeURIComponent(propertyId)}`, {
      headers: facePhotoAuth(), cache: 'no-store',
    }).catch(() => null);
    if (!response?.ok) return;
    const result = await response.json().catch(() => null);
    if (result?.mime_type === 'image/jpeg' && result?.image_base64) setFaceReferencePhoto(`data:image/jpeg;base64,${result.image_base64}`);
  };

  const enrollFaceReference = async () => {
    if (!id || !facePhotoPropertyId || !facePhotoPreview || !facePhotoConsent) return;
    setFacePhotoBusy(true); setFacePhotoError('');
    try {
      const response = await fetch(`${apiOrigin()}/face/enroll`, {
        method: 'POST', headers: facePhotoAuth(),
        body: JSON.stringify({ person_id: id, person_type: 'tenant', property_id: facePhotoPropertyId,
          images: [facePhotoPreview.split(',')[1]], store_reference_photo: true, photo_consent_confirmed: true,
          backend: 'aws_rekognition' }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.error) throw new Error(result.error || result.message || `Face enrollment failed (${response.status})`);
      await loadFaceReference(id, facePhotoPropertyId);
      await reload();
      setFacePhotoPreview(''); setFacePhotoConsent(false);
    } catch (cause) { setFacePhotoError(cause instanceof Error ? cause.message : 'Face enrollment failed'); }
    finally { setFacePhotoBusy(false); }
  };

  const reload = async () => {
    if (!id) return;
    try { setTenant(await getTenantById(id)); }
    catch (e) { console.warn('Reload failed:', e); }
  };

  useEffect(() => {
    if (!facePhotoPropertyId && tenant?.property_id) setFacePhotoPropertyId(String(tenant.property_id));
  }, [tenant?.property_id, facePhotoPropertyId]);

  useEffect(() => {
    if (canManageFaceReference && id && facePhotoPropertyId) void loadFaceReference(id, facePhotoPropertyId);
  }, [id, facePhotoPropertyId, canManageFaceReference]);

  const handleAddProperty = async () => {
    if (!id || !addPropertySelected || !addUnitSelected) return;
    setAddingProperty(true);
    try {
      const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      const res = await fetch(`${API}/people/${id}/properties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ property_id: addPropertySelected, unit_id: addUnitSelected }),
      });
      const data = await res.json();
      setLastAddResult({ code: data.activation_code, property_id: data.property_id });
      await reload();
      setAddPropertyModal(false);
      setAddPropertySelected('');
      setAddUnitSelected('');
    } catch (e) { console.error('Add property failed:', e); }
    finally { setAddingProperty(false); }
  };

  // Load units when property selection changes.
  useEffect(() => {
    if (!addPropertySelected) { setUnitsForSelectedProperty([]); setAddUnitSelected(''); return; }
    (async () => {
      try {
        const list = await apiList('units');
        const filtered = (Array.isArray(list) ? list : []).filter((u: any) =>
          String(u.property_id) === String(addPropertySelected)
        );
        setUnitsForSelectedProperty(filtered);
      } catch (e) { console.warn('Failed to load units:', e); setUnitsForSelectedProperty([]); }
    })();
  }, [addPropertySelected]);

  const handleRevokeActivation = async (activationId: string) => {
    if (!activationId) return;
    if (!window.confirm('Revoke mobile access for this activation? The user will be logged out and the property removed from their dashboard.')) return;
    try {
      const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      const res = await fetch(`${API}/intercom/activations/${activationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.text();
        alert(`Revoke failed: ${err}`);
        return;
      }
      await reload();
    } catch (e) { console.error('Revoke failed:', e); }
  };

  const handleAddCustomer = async () => {
    if (!id || !addCustomerSelected) return;
    setAddingCustomer(true);
    try {
      const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      await fetch(`${API}/people/${id}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ customer_id: addCustomerSelected }),
      });
      await reload();
      setAddCustomerModal(false);
      setAddCustomerSelected('');
    } catch (e) { console.error('Add customer failed:', e); }
    finally { setAddingCustomer(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const t = await getTenantById(id!);
        const [units, props] = await Promise.all([
          getUnits().catch(() => []),
          getProperties().catch(() => []),
        ]);
        const unit = (units || []).find((u: any) => u.id === t.unit_id);
        const prop = (props || []).find((p: any) => p.id === (t.property_id || unit?.property_id));
        const enriched = {
          ...t,
          first_name: t.first_name || t.name?.split(' ')[0] || '',
          last_name: t.last_name || t.name?.split(' ').slice(1).join(' ') || '',
          unit_number: t.unit_number || unit?.unit_number || '',
          property_name: t.property_name || prop?.name || '',
          _property: prop,
        };
        setTenant(enriched);
        setAllProperties(Array.isArray(props) ? props : []);
        setAllUnits(Array.isArray(units) ? units : []);
        try {
          const cs = await apiList('customers');
          setAllCustomers(Array.isArray(cs) ? cs : []);
        } catch (e) { console.warn('Failed to load customers:', e); }

        // Call history — every call_event row where this person is
        // either the tenant being called or the leg owner. Reads
        // /events/calls directly (the app's main CallHistory page
        // uses the same source).
        try {
          const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
          const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
          const res = await fetch(`${API}/events/calls?tenant_id=${id}&limit=100`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const data = await res.json();
            const items = Array.isArray(data) ? data : (data.items || data.calls || data.events || []);
            setCalls(items);
          }
        } catch (e) { console.warn('Failed to load call history:', e); }

        // Call schedules — same shape the iOS app writes via
        // /tenant-self/call-schedules. Read-only here for now.
        setCallSchedules(Array.isArray(t.call_schedules) ? t.call_schedules : []);
        setCallSchedulesUnavailable(false);
        try {
          const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
          const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
          const res = await fetch(`${API}/admin/people/${id}/call-schedules`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const data = await res.json();
            const schedules = Array.isArray(data)
              ? data
              : (data.schedules || data.call_schedules || data.items || []);
            setCallSchedules(Array.isArray(schedules) ? schedules : []);
          } else setCallSchedulesUnavailable(true);
        } catch (e) {
          console.warn('Failed to load call schedules:', e);
          setCallSchedules(Array.isArray(t.call_schedules) ? t.call_schedules : []);
          setCallSchedulesUnavailable(true);
        }

        // Legacy linked-resident records remain readable for compatibility.
        try {
          const all = await apiList('tenants');
          const subs = (Array.isArray(all) ? all : []).filter((p: any) =>
            String(p.parent_person_id || p.primary_tenant_id) === String(id)
            || (p.person_type === 'subtenant' && Array.isArray((tenant as any)?.linked_properties)
                && (tenant as any).linked_properties.some((lp: any) => String(p.unit_id) === String(lp.unit_id) && lp.unit_id))
          );
          setSubtenants(subs);
        } catch (e) { console.warn('Failed to load subtenants:', e); }

        // Fetch tenant_properties (multi-property memberships — filter client-side)
        try {
          const allTp = await apiList('tenant_properties');
          const myTp = (Array.isArray(allTp) ? allTp : []).filter(
            (tp: any) => String(tp.tenant_id) === String(id)
          );
          setTenantProperties(myTp);
        } catch (e) { console.warn('Failed to load tenant_properties:', e); }

        // Load every door (device of type "door"/"lock") on every property
        // this person is linked to so the people page can show which doors
        // their PIN / face / app unlock works on.
        try {
          const linkedPropertyIds = new Set<string>();
          const lps = (enriched?.linked_properties || []) as any[];
          for (const lp of lps) if (lp?.property_id) linkedPropertyIds.add(String(lp.property_id));
          if (enriched?.property_id) linkedPropertyIds.add(String(enriched.property_id));
          if (linkedPropertyIds.size > 0) {
            const allDevices = await apiList('devices');
            const arr = Array.isArray(allDevices) ? allDevices : (allDevices as any)?.items ?? [];
            const doors = arr.filter((d: any) => {
              if (!linkedPropertyIds.has(String(d.property_id))) return false;
              const typeStr = String(d.type ?? d.device_type ?? '').toLowerCase();
              // Lenient match — show anything that looks like a door/lock/reader/strike.
              return /door|lock|reader|strike|panel|relay/.test(typeStr) || /door|lock/.test(String(d.name ?? '').toLowerCase());
            });
            setAccessibleDoors(doors);
          }
        } catch (e) { console.warn('Failed to load accessible doors:', e); }

        // Guest and delivery access codes share guest_pins. Associate them
        // by every creator-key shape emitted by current and migrated clients,
        // then split them by pin_type for the combined person activity tab.
        try {
          const [allGuests, allPasses] = await Promise.all([
            apiList('guest_pins').catch(() => []),
            apiRequestGet('/visitors/passes').catch(() => []),
          ]);
          const legacyRecords = Array.isArray(allGuests)
            ? allGuests
            : ((allGuests as any)?.items || (allGuests as any)?.guest_pins || []);
          const canonicalRecords = (Array.isArray(allPasses) ? allPasses : []).map((pass: any) => ({
            ...pass,
            pin_type: pass.pass_type === 'delivery' ? 'delivery' : 'guest',
            guest_name: pass.visitor_name,
            granted_by_tenant_id: pass.authorized_by,
            expires_at: pass.valid_until,
            created_at: pass.valid_from,
            delivery_notes: pass.delivery_instructions,
            revoked: pass.status === 'revoked',
            is_revoked: pass.status === 'revoked',
          }));
          const records = mergeVisitorPassRecords(canonicalRecords, legacyRecords);
          const created = records.filter((record: any) => wasCreatedByPerson(record, id!));
          setGuestsCreated(created.filter((record: any) => !isDeliveryPin(record)));
          setDeliveriesCreated(created.filter(isDeliveryPin));
        } catch (e) { console.warn('Failed to load visitor passes:', e); }

        // Load cached PDK metadata first, then optionally fetch live
        if (t.pdk_holder_id && t.acs_provider === 'pdk') {
          const cached = t.pdk_metadata_json;
          if (cached) {
            setPdkCached(cached);
            // Build pdkData from cached for immediate display
            setPdkData({
              id: cached.holder_id || t.pdk_holder_id,
              partition: cached.partition,
              firstName: t.first_name,
              lastName: t.last_name,
              email: t.email,
              phone: t.phone,
              enabled: cached.enabled,
              photo: cached.photo_url,
              startDate: cached.start_date,
              endDate: cached.end_date,
              groups: cached.groups || [],
              customFields: cached.custom_fields || {},
              _cached: true,
              _system_name: cached.pdk_system_name,
              _imported_at: cached.imported_at,
            });
          }
        }
      } catch (e) {
        console.error('Failed to load tenant:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const refreshFromPDK = async () => {
    if (!tenant?.pdk_holder_id || !tenant?._property?.pdk_system_id) return;
    setPdkLoading(true);
    setPdkError('');
    try {
      const holder = await getPDKHolder(tenant._property.pdk_system_id, tenant.pdk_holder_id);
      setPdkData({ ...holder, _cached: false });
      setPdkLive(true);
      // Update the cached metadata in Intrlock (non-sensitive only)
      const updatedMeta = {
        pdk_system_id: tenant._property.pdk_system_id,
        pdk_system_name: tenant._property.name,
        holder_id: holder.id,
        partition: holder.partition || null,
        enabled: holder.enabled !== false,
        photo_url: holder.photo || null,
        start_date: holder.startDate || null,
        end_date: holder.endDate || null,
        groups: (holder.groups || []).map((g: any) => ({
          id: g.id, name: g.name, description: g.description || null,
        })),
        custom_fields: holder.customFields || null,
        imported_at: pdkCached?.imported_at || new Date().toISOString(),
        last_synced: new Date().toISOString(),
      };
      await updateTenant(id!, { pdk_metadata_json: updatedMeta });
      refreshAllCounters();
    } catch (e: any) {
      setPdkError(e.message || 'Failed to refresh from PDK');
    } finally {
      setPdkLoading(false);
    }
  };

  // Per-property door-access state — fetched in its own effect so a
  // failure here (or in the giant data-load chain) doesn't strand the
  // Mobile Access toggle in a disabled state.
  useEffect(() => {
    if (!id || accessibleDoors.length === 0) return;
    const propIds = Array.from(new Set(accessibleDoors.map((d) => String(d.property_id)).filter(Boolean)));
    if (propIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const stateByProp: Record<string, PropDoorAccess> = {};
      await Promise.all(propIds.map(async (pid) => {
        try {
          const res: any = await apiGet('people', `${id}/property-doors?property_id=${pid}`);
          const doorList: any[] = Array.isArray(res?.doors) ? res.doors : [];
          stateByProp[pid] = {
            grants:    new Set(doorList.filter((x) => x.granted).map((x) => String(x.id))),
            hides:     new Set(doorList.filter((x) => x.hidden).map((x) => String(x.id))),
            baseline:  new Set(doorList.filter((x) => x.baseline).map((x) => String(x.id))),
            nameByKey: new Map(doorList.map((x) => [String(x.id), String(x.name ?? 'Door')])),
          };
        } catch (e) {
          console.error('[TenantDetail] door access load failed for', pid, e);
        }
      }));
      if (!cancelled) {
        setDoorAccessByProp(stateByProp);
        // Deep-copy snapshot of the initial state for dirty tracking.
        const snap: Record<string, PropDoorAccess> = {};
        for (const [k, v] of Object.entries(stateByProp)) {
          snap[k] = {
            grants:    new Set(v.grants),
            hides:     new Set(v.hides),
            baseline:  new Set(v.baseline),
            nameByKey: new Map(v.nameByKey),
          };
        }
        setDoorAccessOrigByProp(snap);
      }
    })();
    return () => { cancelled = true; };
  }, [id, accessibleDoors]);

  const handleUpdate = async (data: any) => {
    try {
      await updateTenant(id!, data);
      refreshAllCounters();
      setTenant({ ...tenant, ...data });
      setEditModal(false);
    } catch (e) {
      console.error('Failed to update tenant:', e);
    }
  };

  /// Flip a door's Mobile Access for THIS person on the given property.
  /// Local-only — does not save until the Save button is clicked. If the
  /// page-load fetch missed for this property, lazy-fetch state first so
  /// the toggle has a baseline to work against.
  const toggleTenantDoorAccess = async (propertyId: string, doorKey: string) => {
    if (!id) return;

    let cur = doorAccessByProp[propertyId];
    if (!cur) {
      try {
        const res: any = await apiGet('people', `${id}/property-doors?property_id=${propertyId}`);
        const doorList: any[] = Array.isArray(res?.doors) ? res.doors : [];
        cur = {
          grants:    new Set(doorList.filter((x: any) => x.granted).map((x: any) => String(x.id))),
          hides:     new Set(doorList.filter((x: any) => x.hidden).map((x: any) => String(x.id))),
          baseline:  new Set(doorList.filter((x: any) => x.baseline).map((x: any) => String(x.id))),
          nameByKey: new Map(doorList.map((x: any) => [String(x.id), String(x.name ?? 'Door')])),
        };
        setDoorAccessByProp((prev) => ({ ...prev, [propertyId]: cur! }));
        // Snapshot the just-loaded state too so dirty-tracking starts fresh.
        setDoorAccessOrigByProp((prev) => ({
          ...prev,
          [propertyId]: {
            grants:    new Set(cur!.grants),
            hides:     new Set(cur!.hides),
            baseline:  new Set(cur!.baseline),
            nameByKey: new Map(cur!.nameByKey),
          },
        }));
      } catch (e) {
        console.error('[TenantDetail] on-demand state load failed:', e);
        alert('Failed to load door state for this property.');
        return;
      }
    }

    const inBaseline = cur.baseline.has(doorKey);
    const wasGranted = cur.grants.has(doorKey);
    const wasHidden  = cur.hides.has(doorKey);
    const wasEffective = (inBaseline || wasGranted) && !wasHidden;
    const turningOn = !wasEffective;

    const nextGrants = new Set(cur.grants);
    const nextHides  = new Set(cur.hides);
    if (turningOn) {
      nextHides.delete(doorKey);
      if (!inBaseline) nextGrants.add(doorKey);
    } else {
      nextGrants.delete(doorKey);
      if (inBaseline) nextHides.add(doorKey);
    }

    setDoorAccessByProp((prev) => ({
      ...prev,
      [propertyId]: { ...cur!, grants: nextGrants, hides: nextHides },
    }));
  };

  /// Diff current state against the load snapshot to find dirty properties.
  const setEquals = (a: Set<string>, b: Set<string>): boolean => {
    if (a.size !== b.size) return false;
    const aArr = Array.from(a);
    for (let i = 0; i < aArr.length; i++) if (!b.has(aArr[i])) return false;
    return true;
  };
  const dirtyPropertyIds = (() => {
    const dirty: string[] = [];
    for (const pid of Object.keys(doorAccessByProp)) {
      const cur = doorAccessByProp[pid];
      const orig = doorAccessOrigByProp[pid];
      if (!orig || !setEquals(cur.grants, orig.grants) || !setEquals(cur.hides, orig.hides)) {
        dirty.push(pid);
      }
    }
    return dirty;
  })();

  const saveDoorAccessChanges = async () => {
    if (!id || dirtyPropertyIds.length === 0) return;
    setDoorAccessSaving(true);
    const results: { propertyId: string; ok: boolean; err?: any }[] = [];
    for (const pid of dirtyPropertyIds) {
      const cur = doorAccessByProp[pid];
      try {
        await apiUpdate('people', `${id}/property-doors`, {
          property_id: pid,
          door_grants: Array.from(cur.grants),
          door_hides: Array.from(cur.hides),
        });
        results.push({ propertyId: pid, ok: true });
      } catch (err) {
        console.error('[TenantDetail] save failed for', pid, err);
        results.push({ propertyId: pid, ok: false, err });
      }
    }
    // Update snapshot for properties that saved successfully so the dirty
    // count clears; leave failed ones marked dirty so the user can retry.
    setDoorAccessOrigByProp((prev) => {
      const next = { ...prev };
      for (const r of results) {
        if (!r.ok) continue;
        const cur = doorAccessByProp[r.propertyId];
        next[r.propertyId] = {
          grants:    new Set(cur.grants),
          hides:     new Set(cur.hides),
          baseline:  new Set(cur.baseline),
          nameByKey: new Map(cur.nameByKey),
        };
      }
      return next;
    });
    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      alert(`${results.length - failed.length} saved, ${failed.length} failed. Try again.`);
    }
    setDoorAccessSaving(false);
  };

  const handlePermUpdate = async (field: string, value: any) => {
    setPermSaving(true);
    try {
      await apiUpdate('tenants', id!, { [field]: value });
      refreshAllCounters();
      setTenant((prev: any) => ({ ...prev, [field]: value }));
    } catch (e) {
      console.error('Failed to update permission:', e);
      alert('Failed to save permission.');
    } finally {
      setPermSaving(false);
    }
  };

  const updatePropertyRight = async (tp: any, field: string, value: boolean | number) => {
    const linkId = tp.tenant_property_id || tp.id;
    if (!linkId) return alert('This property assignment must be refreshed before permissions can be changed.');
    setPermSaving(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://shadow.api.intrlock.io';
      const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      const response = await fetch(`${apiBase}/tenant-properties/${linkId}/permissions`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      const saved = await response.json().catch(() => null);
      if (!response.ok) throw new Error(saved?.error || 'Permission was not saved');
      if (saved?.permissions?.[field] !== value) throw new Error('Permission did not persist. Refresh this person and try again.');
      setTenant((previous: any) => ({ ...previous, linked_properties: (previous.linked_properties || []).map((row: any) =>
        String(row.tenant_property_id || row.id) === String(linkId)
          ? { ...row, permissions: { ...(row.permissions || {}), ...saved.permissions } }
          : row,
      ) }));
    } catch (error: any) {
      alert(error?.message || 'Permission was not saved');
    } finally { setPermSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  if (!tenant) return <div className="text-center py-12 text-gray-400">Person not found</div>;

  const isPDK = !!(tenant.pdk_holder_id && tenant.acs_provider === 'pdk');

  return (
    <div className="person-detail-page">
      <button onClick={() => navigate('/tenants')} className="person-back flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>Back to People
      </button>
      <section className="identity person-identity">
        <div className="mark" aria-hidden="true"><span>{(tenant.first_name?.[0] || '') + (tenant.last_name?.[0] || '')}</span></div>
        <div><small>Person record</small><h2>{tenant.first_name} {tenant.last_name} <Badge status={tenant.status || 'active'}>{tenant.status || 'active'}</Badge></h2><p>{[tenant.email, tenant.phone].filter(Boolean).join(' · ') || 'No contact information'}</p></div>
        <div><Button onClick={() => setEditModal(true)}>Edit Person</Button></div>
      </section>
      <section className="detail-stats" aria-label="Person totals">
        <div><span>Properties</span><strong>{(tenant.linked_properties || []).length || (tenant.property_id ? 1 : 0)}</strong></div>
        <div><span>Assigned units</span><strong>{new Set((tenant.linked_properties || []).map((p: any) => p.unit_id).filter(Boolean)).size || (tenant.unit_id ? 1 : 0)}</strong></div>
        <div><span>Accessible doors</span><strong>{accessibleDoors.length}</strong></div>
        <div><span>Guest passes</span><strong>{guestsCreated.length}</strong></div>
        <div><span>Deliveries</span><strong>{deliveriesCreated.length}</strong></div>
        <div><span>Calls</span><strong>{calls.length}</strong></div>
      </section>
      <nav className="tabs person-tabs" aria-label="Person detail sections">
        {[
          ['overview','Overview'],['assignments','Properties & Units'],['access','Access'],['mobile','Mobile'],['guests','Guests & Deliveries'],['calls','Calls'],['schedules','Schedules'],
        ].map(([key, label]) => <button key={key} type="button" className={detailTab === key ? 'active' : ''} aria-selected={detailTab === key} onClick={() => setDetailTab(key)}>{label}</button>)}
      </nav>
      <div className={`mb-6 ${detailTab === 'overview' ? '' : 'hidden'}`}>
        <EntityUsageCard entityType="person" entityId={(tenant as any)?.uuid || (tenant as any)?.id || id} />
      </div>
      <div className={`person-detail-grid ${detailTab === 'overview' ? 'person-detail-grid-overview' : 'person-detail-grid-wide'} grid grid-cols-1 gap-6`}>
        <Card id="person-overview-legacy" className="hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{tenant.first_name} {tenant.last_name}</h2>
            <Badge status={tenant.status || 'active'}>{tenant.status || 'active'}</Badge>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="text-gray-500">Email:</span> <span className="text-gray-900">{tenant.email || '\u2014'}</span></div>
            <div><span className="text-gray-500">Phone:</span> <span className="text-gray-900">{tenant.phone || '\u2014'}</span></div>
            {/* Property + Unit removed from header \u2014 full breakdown lives in
                the Assigned Units table below so the data is the single source
                of truth and never falls out of sync with multi-property rows. */}
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Role:</span>
              <Badge color="green">Tenant</Badge>
            </div>
            {tenant.move_in_date && <div><span className="text-gray-500">Move-in:</span> <span>{tenant.move_in_date}</span></div>}
            {/* §28.518.99.48 — PM per-tenant toggle for delivery-code creation.
                Backed by people.can_receive_deliveries (default true). */}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 text-sm font-medium">Can receive deliveries</div>
                <div className="text-xs text-gray-500">Allow this person to be the recipient of a delivery PIN</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={tenant.can_receive_deliveries !== false}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    try {
                      await updateTenant(id!, { can_receive_deliveries: next });
                      setTenant((t: any) => ({ ...t, can_receive_deliveries: next }));
                      refreshAllCounters();
                    } catch (err) {
                      console.error('Failed to update can_receive_deliveries:', err);
                      alert('Delivery permission was not saved. Please try again.');
                    }
                  }}
                />
                <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-600" />
              </label>
            </div>
            {isPDK && (
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <Badge color="blue">PDK</Badge>
                  <span className="text-xs text-gray-400 font-mono">Holder #{tenant.pdk_holder_id}</span>
                </div>
              </div>
            )}
          </div>

          <Button variant="secondary" className="w-full mt-4" onClick={() => setEditModal(true)}>Edit Person</Button>
        </Card>
        <article id="person-overview" className={`customer-grid-card person-mirror-card bg-white border border-gray-200 ${detailTab === 'overview' ? '' : 'hidden'}`}>
          <div className="person-mirror-heading">
            <div className="person-photo" aria-label={faceReferencePhoto || tenant.photo_url ? `${tenant.first_name || ''} ${tenant.last_name || ''} profile photo`.trim() : 'Person profile silhouette'}>
              <svg className="person-silhouette" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm-8 9a8 8 0 0 1 16 0H4Z" /></svg>
              {faceReferencePhoto || tenant.photo_url ? <img src={faceReferencePhoto || tenant.photo_url} alt={`${tenant.first_name || ''} ${tenant.last_name || ''}`.trim()} onError={(event) => { event.currentTarget.style.display = 'none'; }} /> : null}
            </div>
            <div><h3>{tenant.first_name} {tenant.last_name}</h3><p>{tenant.person_type || tenant.role || 'Tenant'}</p></div>
            <Badge status={tenant.status || 'active'}>{tenant.status || 'active'}</Badge>
          </div>
          <div className="customer-card-address"><span>Email</span>{tenant.email ? <a href={`mailto:${tenant.email}`}>{tenant.email}</a> : <p>Not provided</p>}</div>
          <div className="customer-card-contact"><span>Phone</span>{tenant.phone ? <a href={`tel:${tenant.phone}`}>{tenant.phone}</a> : <p>Not provided</p>}</div>
          <details className="customer-card-metrics"><summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌃</b></summary><div><article><span>Properties</span><strong>{(tenant.linked_properties || []).length}</strong></article><article><span>Units</span><strong>{new Set((tenant.linked_properties || []).map((p:any)=>p.unit_id).filter(Boolean)).size}</strong></article><article><span>Devices</span><strong>{accessibleDoors.length}</strong></article><article><span>Guests</span><strong>{guestsCreated.length}</strong></article><article><span>Deliveries</span><strong>{deliveriesCreated.length}</strong></article><article><span>Calls</span><strong>{calls.length}</strong></article></div></details>
          <div className="person-card-actions"><Button variant="secondary" onClick={() => tenant.email ? window.location.href=`mailto:${tenant.email}` : tenant.phone ? window.location.href=`tel:${tenant.phone}` : undefined}>Contact</Button><Button onClick={() => setDetailTab('mobile')}>Send Code</Button><Button variant="secondary" onClick={() => setEditModal(true)}>Edit Person</Button></div>
          <div className="border-t border-gray-300 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-600">Tenant access PIN</p>
            <PinCell tenantId={tenant.id} savedPin={tenant.access_pin} width={110} onSaved={(accessPin) => setTenant((current: any) => ({ ...current, access_pin: accessPin }))} />
          </div>
          {canManageFaceReference && <div className="border-t border-gray-300 p-4 text-sm">
            <h4 className="font-semibold text-gray-900">Face ID reference photo</h4>
            <p className="mt-1 text-xs text-gray-600">Upload a consented photo for this person's Face ID enrollment at a linked property. The reference image is kept private.</p>
            <select aria-label="Face ID property" value={facePhotoPropertyId} onChange={event => setFacePhotoPropertyId(event.target.value)} className="mt-3 w-full border border-gray-300 bg-white px-3 py-2">
              <option value="">Select property</option>
              {Array.from(new Set<string>([tenant.property_id, ...(tenant.linked_properties || []).map((row: any) => row.property_id)].filter(Boolean).map(String))).map(propertyId =>
                <option key={propertyId} value={propertyId}>{allProperties.find((property: any) => String(property.id) === propertyId)?.name || propertyId}</option>)}
            </select>
            <input aria-label="Choose Face ID reference photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={event => void prepareFacePhoto(event.target.files?.[0])} className="mt-2 block w-full text-xs" />
            {facePhotoPreview && <img src={facePhotoPreview} alt="Face ID reference preview" className="mt-2 max-h-48 max-w-full border border-gray-300 object-contain" />}
            <label className="mt-2 flex items-start gap-2 text-xs text-gray-700"><input type="checkbox" checked={facePhotoConsent} onChange={event => setFacePhotoConsent(event.target.checked)} /> I confirm this person consented to biometric enrollment and use of this photo as a Face ID reference.</label>
            {facePhotoError && <p role="alert" className="mt-2 text-red-700">{facePhotoError}</p>}
            <button type="button" disabled={facePhotoBusy || !facePhotoPropertyId || !facePhotoPreview || !facePhotoConsent} onClick={enrollFaceReference} className="mt-3 border border-gray-400 bg-gray-900 px-3 py-2 font-semibold text-white disabled:opacity-50">{facePhotoBusy ? 'Enrolling…' : 'Save photo and enroll Face ID'}</button>
          </div>}
        </article>
        <div className={`${detailTab === 'overview' ? 'lg:col-span-2' : ''} space-y-6`}>
          {/* PDK Information Panel */}
          {isPDK && (
            <Card className={detailTab === 'overview' ? '' : 'hidden'}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">ProDataKey Information</h3>
                  {pdkData?._cached && !pdkLive && (
                    <span className="text-xs text-gray-400">(cached{pdkCached?.last_synced ? ` ${new Date(pdkCached.last_synced).toLocaleDateString()}` : pdkCached?.imported_at ? ` ${new Date(pdkCached.imported_at).toLocaleDateString()}` : ''})</span>
                  )}
                  {pdkLive && <span className="text-xs text-green-600 font-medium">Live</span>}
                </div>
                <div className="flex items-center gap-2">
                  {pdkData && (
                    <Badge color={pdkData.enabled !== false ? 'green' : 'red'}>
                      {pdkData.enabled !== false ? 'Enabled' : 'Disabled'}
                    </Badge>
                  )}
                  <Button size="sm" variant="secondary" onClick={refreshFromPDK} loading={pdkLoading}>
                    Refresh from PDK
                  </Button>
                </div>
              </div>
              {pdkData?._system_name && (
                <p className="text-xs text-gray-500 mb-3">System: <span className="font-medium text-gray-700">{pdkData._system_name}</span></p>
              )}

              {pdkLoading ? (
                <div className="py-6 text-center text-gray-400 text-sm">Loading PDK holder data...</div>
              ) : pdkError ? (
                <div className="py-4 text-center text-red-500 text-sm">{pdkError}</div>
              ) : pdkData ? (
                <>
                  {/* PDK Sub-tabs */}
                  <div className="flex border-b border-gray-200 mb-4 -mx-5 px-5">
                    {(['overview', 'groups', 'custom'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setPdkTab(t)}
                        className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors capitalize ${
                          pdkTab === t ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {t === 'custom' ? 'Custom Fields' : t}
                      </button>
                    ))}
                  </div>

                  {pdkTab === 'overview' && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Holder ID</p>
                        <p className="font-mono text-gray-900 mt-1">{pdkData.id}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Partition</p>
                        <p className="text-gray-900 mt-1">{pdkData.partition || '\u2014'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">First Name</p>
                        <p className="text-gray-900 mt-1">{pdkData.firstName || '\u2014'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Last Name</p>
                        <p className="text-gray-900 mt-1">{pdkData.lastName || '\u2014'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                        <p className="text-gray-900 mt-1">{pdkData.email || '\u2014'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                        <p className="text-gray-900 mt-1">{pdkData.phone || pdkData.mobilePhone || '\u2014'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                        <div className="mt-1">
                          <Badge color={pdkData.enabled !== false ? 'green' : 'red'}>
                            {pdkData.enabled !== false ? 'Active' : 'Disabled'}
                          </Badge>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Photo</p>
                        <p className="text-gray-900 mt-1">{pdkData.photo ? 'Yes' : 'None'}</p>
                      </div>
                      {pdkData.startDate && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Start Date</p>
                          <p className="text-gray-900 mt-1">{new Date(pdkData.startDate).toLocaleDateString()}</p>
                        </div>
                      )}
                      {pdkData.endDate && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">End Date</p>
                          <p className="text-gray-900 mt-1">{new Date(pdkData.endDate).toLocaleDateString()}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {pdkTab === 'groups' && (
                    <div>
                      {pdkData.groups && pdkData.groups.length > 0 ? (
                        <div className="space-y-2">
                          {pdkData.groups.map((g: any, i: number) => (
                            <div key={g.id || i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{g.name || `Group ${g.id}`}</p>
                                {g.description && <p className="text-xs text-gray-500 mt-0.5">{g.description}</p>}
                              </div>
                              <div className="flex items-center gap-2">
                                {g.partition && <span className="text-xs text-gray-400">{g.partition}</span>}
                                <Badge color="blue">Group</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-6 text-center text-gray-400 text-sm">No groups assigned in PDK</div>
                      )}
                    </div>
                  )}

                  {pdkTab === 'custom' && (
                    <div>
                      {pdkData.customFields && Object.keys(pdkData.customFields).length > 0 ? (
                        <div className="space-y-2">
                          {Object.entries(pdkData.customFields).map(([key, value]: [string, any]) => (
                            <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">{key}</span>
                              <span className="text-sm font-medium text-gray-900">{String(value) || '\u2014'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-6 text-center text-gray-400 text-sm">No custom fields configured in PDK</div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="py-4 text-center text-gray-400 text-sm">No PDK data available</div>
              )}
            </Card>
          )}

          {/* Linked Customers (parent organizational scope) */}
          <Card id="person-overview-customers" className="hidden" padding={false}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold">Customers ({(tenant.linked_customers || []).length})</h3>
              <Button size="sm" onClick={() => setAddCustomerModal(true)}>Assign Customer</Button>
            </div>
            {(tenant.linked_customers || []).length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">Not linked to any customer yet.</div>
            ) : (
              <Table
                columns={[
                  { key: 'customer_name', header: 'Customer', render: (c: any) => <span className="font-medium text-gray-900">{c.customer_name}</span> },
                  { key: 'role', header: 'Role', render: (c: any) => <Badge color="blue">{c.role || 'tenant'}</Badge> },
                  { key: 'status', header: 'Status', render: (c: any) => <Badge color={c.status === 'active' ? 'green' : 'gray'}>{c.status}</Badge> },
                ]}
                data={tenant.linked_customers}
              />
            )}
          </Card>

          {detailTab === 'assignments' && <div className="person-property-card-list">
            <div className="person-section-heading"><div><h3>Properties</h3><p>Property assignments, units, and rights for this person.</p></div><Button size="sm" onClick={() => setAddPropertyModal(true)}>Add Property</Button></div>
            {(() => { const byPid = new Map<string, any>(); (tenant.linked_properties || []).forEach((lp:any) => { const pid=String(lp.property_id); const prev=byPid.get(pid)||{}; byPid.set(pid,{...prev,...lp}); }); if(tenant.property_id&&!byPid.has(String(tenant.property_id))) byPid.set(String(tenant.property_id),{property_id:tenant.property_id,unit_id:tenant.unit_id,status:tenant.status}); const rows=Array.from(byPid.values()); return rows.length ? rows.map((tp:any) => { const prop=allProperties.find((p:any)=>String(p.id)===String(tp.property_id))||{}; const unit=allUnits.find((u:any)=>String(u.id)===String(tp.unit_id))||{}; const perms=tp.permissions||{}; return <article className="person-property-card" key={tp.property_id}><div className="person-property-top"><div className="person-property-icon" aria-hidden="true">⌂</div><div><h3>{prop.name||tp.property_name||'Property'}</h3><p>{[prop.address,prop.city,prop.state,prop.zip].filter(Boolean).join(', ')||'Address unavailable'}</p><span>{prop.telnyx_number||prop.phone_number||'No intercom phone'}</span></div><button onClick={()=>navigate(`/properties/${prop.uuid||prop.id||tp.property_id}`)}>Open ↗</button></div><div className="person-unit-box"><div><span>Unit</span><strong>{tp.unit_number||unit.unit_number||'Not assigned'}</strong></div><div><span>Floor</span><strong>{unit.floor||tp.floor||'—'}</strong></div><div><span>Role</span><strong>{tp.role||tenant.person_type||'Tenant'}</strong></div><div><span>Status</span><strong>{tp.status||'active'}</strong></div></div><div className="person-rights-box"><label><span>Can create tenants</span><button type="button" className={`person-switch ${perms.can_add_tenants?'on':''}`} disabled={permSaving} onClick={()=>updatePropertyRight(tp,'can_add_tenants',!perms.can_add_tenants)} role="switch" aria-checked={!!perms.can_add_tenants}><i /></button></label><label><span>Can create guests</span><button type="button" className={`person-switch ${perms.can_add_guests?'on':''}`} disabled={permSaving} onClick={()=>updatePropertyRight(tp,'can_add_guests',!perms.can_add_guests)} role="switch" aria-checked={!!perms.can_add_guests}><i /></button></label><label><span>Can create deliveries</span><button type="button" className={`person-switch ${perms.can_issue_delivery_codes?'on':''}`} disabled={permSaving} onClick={()=>updatePropertyRight(tp,'can_issue_delivery_codes',!perms.can_issue_delivery_codes)} role="switch" aria-checked={!!perms.can_issue_delivery_codes}><i /></button></label><label className="person-stepper"><span>Mobile app activations</span><div><button type="button" disabled={permSaving||(Number(perms.mobile_device_activation_limit??tp.mobile_device_activation_limit??1)<=0)} onClick={()=>updatePropertyRight(tp,'mobile_device_activation_limit',Math.max(0,Number(perms.mobile_device_activation_limit??tp.mobile_device_activation_limit??1)-1))}>−</button><strong>{Number(perms.mobile_device_activation_limit??tp.mobile_device_activation_limit??1)}</strong><button type="button" disabled={permSaving} onClick={()=>updatePropertyRight(tp,'mobile_device_activation_limit',Number(perms.mobile_device_activation_limit??tp.mobile_device_activation_limit??1)+1)}>+</button></div></label></div></article>; }) : <div className="person-empty-card">No properties assigned.</div>; })()}
          </div>}

          {/* ── Assigned Units ───────────────────────────────────────────────
              Single source of truth = `tenant.linked_properties` returned by
              /people/:id (already includes unit_id + unit_number + property
              name). We merge in the legacy primary property/unit when it
              isn't already represented, then dedupe by unit_id+property_id
              so a person never appears twice for the same unit and so the
              Units table can never disagree with the Properties table below. */}
          {(() => {
            const rows = (() => {
              const out: { unit: any; property: any; status: string; tp_id?: string }[] = [];
              const seen = new Set<string>();
              const linked = (tenant?.linked_properties || []) as any[];
              for (const lp of linked) {
                const u = allUnits.find((x: any) => x.id === lp.unit_id) || (lp.unit_id ? { id: lp.unit_id, unit_number: lp.unit_number, floor: lp.floor } : null);
                const p = allProperties.find((x: any) => x.id === lp.property_id) || (lp.property_id ? { id: lp.property_id, name: lp.property_name } : null);
                const key = `${lp.unit_id || u?.id || 'no-unit'}-${lp.property_id || p?.id || 'no-prop'}`;
                if (seen.has(key)) continue;
                seen.add(key);
                out.push({ unit: u, property: p, status: lp.status || 'active', tp_id: lp.tenant_property_id });
              }
              // Legacy primary unit fallback (older single-property records)
              if (tenant?.unit_id) {
                const u = allUnits.find((x: any) => x.id === tenant.unit_id);
                const p = allProperties.find((x: any) => x.id === (u?.property_id || tenant.property_id));
                const key = `${tenant.unit_id}-${p?.id || 'no-prop'}`;
                if (!seen.has(key)) {
                  seen.add(key);
                  out.push({ unit: u, property: p, status: tenant.status || 'active' });
                }
              }
              return out;
            })();
            return (
              <Card id="person-assignments" className="hidden" padding={false}>
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold">Assigned Units ({rows.length})</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Every unit this person occupies across all of their properties.</p>
                </div>
                {rows.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm">No units assigned.</div>
                ) : (
                  <Table
                    columns={[
                      {
                        key: 'unit',
                        header: 'Unit',
                        render: (r: any) => (
                          <span className="font-medium text-gray-900">
                            {r.unit?.unit_number ? `#${r.unit.unit_number}` : <span className="text-gray-400">— unassigned —</span>}
                          </span>
                        ),
                      },
                      {
                        key: 'property',
                        header: 'Property',
                        render: (r: any) => r.property
                          ? (
                            <button
                              onClick={() => navigate(`/properties/${r.property.uuid || r.property.id}`)}
                              className="text-brand-600 hover:underline text-left"
                            >{r.property.name}</button>
                          )
                          : <span className="text-gray-400">—</span>,
                      },
                      {
                        key: 'floor',
                        header: 'Floor',
                        render: (r: any) => r.unit?.floor ?? <span className="text-gray-400">—</span>,
                      },
                      {
                        key: 'role',
                        header: 'Role',
                        render: () => <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Tenant</span>,
                      },
                      {
                        key: 'status',
                        header: 'Status',
                        render: (r: any) => <Badge status={r.status}>{r.status}</Badge>,
                      },
                      {
                        key: 'actions',
                        header: '',
                        render: (r: any) => r.unit ? (
                          <button
                            onClick={() => r.property && navigate(`/properties/${r.property.uuid || r.property.id}?tab=units`)}
                            className="text-xs font-medium text-brand-600 hover:text-brand-700"
                          >View</button>
                        ) : null,
                      },
                    ]}
                    data={rows.map((r, i) => ({ ...r, id: i }))}
                  />
                )}
              </Card>
            );
          })()}

          {/* Per-property permissions — one row per assigned property. */}
          {(() => {
            const linked = (tenant?.linked_properties || []) as any[];
            if (!linked.length) return null;
            const byProp = new Map<string, any>();
            for (const lp of linked) {
              const pid = String(lp.property_id || '');
              if (!pid) continue;
              if (!byProp.has(pid)) byProp.set(pid, lp);
            }
            const grouped = Array.from(byProp.values());

            const togglePerm = async (tpId: string, _propId: string, field: string, value: boolean) => {
              const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
              try {
                await fetch(`${apiBase}/tenant-properties/${tpId}/permissions`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('intrlock_token') || ''}`,
                  },
                  body: JSON.stringify({ [field]: value }),
                });
                // Optimistic local update
                setTenant((prev: any) => prev ? {
                  ...prev,
                  linked_properties: (prev.linked_properties || []).map((lp: any) =>
                    lp.tenant_property_id === tpId
                      ? { ...lp, permissions: { ...(lp.permissions || {}), [field]: value } }
                      : lp,
                  ),
                } : prev);
              } catch (err) {
                console.error('Failed to update permission:', err);
              }
            };

            const Switch = ({ on, onChange, disabled }: { on: boolean; onChange: () => void; disabled?: boolean }) => (
              <button
                type="button"
                disabled={disabled}
                onClick={onChange}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-gray-300'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${on ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            );

            return (
              <Card className="hidden" padding={false}>
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold">Permissions per Property ({grouped.length})</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Property managers set what this tenant can do at each building. Changes save instantly.</p>
                </div>
                <Table
                  columns={[
                    {
                      key: 'property',
                      header: 'Property',
                      render: (r: any) => {
                        const prop = allProperties.find((x: any) => x.id === r.property_id);
                        return (
                          <button
                            onClick={() => prop && navigate(`/properties/${prop.uuid || prop.id}`)}
                            className="text-brand-600 hover:underline text-left font-medium"
                          >
                            {prop?.name || r.property_name || '—'}
                          </button>
                        );
                      },
                    },
                    {
                      key: 'unit',
                      header: 'Unit',
                      render: (r: any) => r.unit_number ? <span className="font-mono text-sm">#{r.unit_number}</span> : <span className="text-gray-400">—</span>,
                    },
                    {
                      key: 'p_guests',
                      header: 'Add Guests',
                      render: (r: any) => <Switch on={!!r.permissions?.can_add_guests} onChange={() => togglePerm(r.tenant_property_id, r.property_id, 'can_add_guests', !r.permissions?.can_add_guests)} />,
                    },
                    {
                      key: 'p_deliveries',
                      header: 'Delivery Codes',
                      render: (r: any) => <Switch on={!!r.permissions?.can_issue_delivery_codes} onChange={() => togglePerm(r.tenant_property_id, r.property_id, 'can_issue_delivery_codes', !r.permissions?.can_issue_delivery_codes)} />,
                    },
                    {
                      key: 'acs_link',
                      header: 'ACS Holder',
                      render: (r: any) => {
                        // No ACS system attached to this property → nothing to verify.
                        if (!r.acs_system_id) return <span className="text-xs text-gray-400 italic">No ACS system</span>;
                        // ACS present, holder linked → show short holder id with full id in title.
                        if (r.acs_holder_id) {
                          const short = String(r.acs_holder_id).slice(0, 8);
                          return (
                            <span
                              className="inline-flex items-center gap-1.5 font-mono text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded"
                              title={`Holder ID: ${r.acs_holder_id}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              {short}…
                            </span>
                          );
                        }
                        // ACS present, no holder → flag so PM can re-provision.
                        return (
                          <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Not linked
                          </span>
                        );
                      },
                    },
                  ]}
                  data={grouped.map((g, i) => ({ ...g, id: g.tenant_property_id || i }))}
                />
              </Card>
            );
          })()}

          {/* Mobile App Activations */}
          <Card id="person-mobile" className={detailTab === 'mobile' ? '' : 'hidden'} padding={false}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">Mobile App Activations ({(tenant.activations || []).length})</h3>
              <p className="text-xs text-gray-400 mt-0.5">Activation codes, device fingerprints, VoIP tokens, and online state per device.</p>
            </div>
            {(tenant.activations || []).length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No mobile activations yet.</div>
            ) : (
              <Table
                columns={[
                  {
                    key: 'property',
                    header: 'Property',
                    render: (a: any) => {
                      const prop = allProperties.find((p: any) => p.id === a.property_id);
                      return <span className="text-sm text-gray-700">{prop?.name || a.property_id?.slice(0, 8) || '—'}</span>;
                    },
                  },
                  {
                    key: 'unit',
                    header: 'Unit',
                    render: (a: any) => {
                      const matchingAssignments = (tenant.linked_properties || []).filter(
                        (assignment: any) => String(assignment.property_id) === String(a.property_id),
                      );
                      const unitId = a.unit_id || (matchingAssignments.length === 1 ? matchingAssignments[0].unit_id : null);
                      const unit = allUnits.find((item: any) => String(item.id) === String(unitId));
                      const unitNumber = unit?.unit_number || (matchingAssignments.length === 1 ? matchingAssignments[0].unit_number : null);
                      return <span className="text-sm text-gray-700">{unitNumber ? `#${unitNumber}` : '—'}</span>;
                    },
                  },
                  {
                    key: 'activation_code',
                    header: 'Code',
                    render: (a: any) => <SensitiveCredential value={a.activation_code} />,
                  },
                  {
                    key: 'activated',
                    header: 'Activated',
                    render: (a: any) => (
                      <div className="flex flex-col gap-1 items-start">
                        <Badge color={a.activated ? 'green' : 'gray'}>{a.activated ? 'Yes' : 'Pending'}</Badge>
                        {a.activated && (
                          a.activated_at
                            ? <time dateTime={a.activated_at} className="text-xs text-gray-500">{formatDateTime(a.activated_at)}</time>
                            : <span className="text-xs text-gray-500">Time unavailable</span>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'device_type',
                    header: 'Device',
                    render: (a: any) => (
                      <div className="text-xs text-gray-600">
                        <p>{formatDeviceType(a.device_type, '—')} {a.device_model || ''}</p>
                        {a.device_os && <p className="text-gray-400">{a.device_os}</p>}
                        <p className="text-gray-400">App version: {a.app_version || 'unreported'}</p>
                      </div>
                    ),
                  },
                  {
                    key: 'voip',
                    header: 'VoIP Token',
                    render: (a: any) => (
                      <Badge color={a.voip_token_present ? 'green' : 'gray'}>
                        {a.voip_token_present ? 'present' : 'missing'}
                      </Badge>
                    ),
                  },
                  {
                    key: 'fingerprint',
                    header: 'Device ID',
                    render: (a: any) => {
                      // Stable per-device identifier from the backend. Most rows
                      // have device_uuid populated even when device_fingerprint
                      // is null — fall through to id as a last resort so the
                      // column always shows something useful.
                      const did = a.device_fingerprint || a.device_uuid || a.id;
                      if (!did) return <span className="text-gray-400">—</span>;
                      const short = String(did).slice(0, 8);
                      return (
                        <span
                          className="font-mono text-xs text-gray-500 cursor-help"
                          title={String(did)}
                        >{short}…</span>
                      );
                    },
                  },
                  {
                    key: 'actions',
                    header: '',
                    render: (a: any) => (
                      <div className="flex items-center gap-3 justify-end">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const propertyId = a.property_id || tenant.property_id || tenant._property?.id;
                              if (!propertyId) { alert('No property assigned'); return; }
                              const result = await generateActivationCode(tenant.legacy_id || tenant.id, propertyId);
                              const newCode = (result as any).activation_code || (result as any).code;
                              if (!newCode) throw new Error('No code returned');
                              // Optimistic local update so the new code is visible
                              // immediately without a full reload.
                              setTenant((prev: any) => prev ? {
                                ...prev,
                                activations: (prev.activations || []).map((row: any) =>
                                  row.id === a.id
                                    ? { ...row, activation_code: newCode, activated: false, voip_token_present: false }
                                    : row,
                                ),
                              } : prev);
                              try { await navigator.clipboard.writeText(newCode); } catch { /* clipboard noise */ }
                              alert(`New activation code: ${newCode}\n\nCopied to clipboard. Share with the tenant to re-link their app.`);
                            } catch (e: any) {
                              alert(e?.message || 'Failed to regenerate code');
                            }
                          }}
                          className="text-xs font-medium text-brand-600 hover:text-brand-700"
                        >
                          Regenerate
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRevokeActivation(a.id)}
                          className="text-xs font-medium text-red-600 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      </div>
                    ),
                  },
                ]}
                data={tenant.activations}
              />
            )}
          </Card>

          {/* Properties */}
          <Card id="person-assignments-properties" className="hidden" padding={false}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold">Properties</h3>
              <Button size="sm" onClick={() => setAddPropertyModal(true)}>Add Property</Button>
            </div>
            {(() => {
              // Source of truth is `tenant.linked_properties` (returned
              // by /people/:id), which already merges the primary
              // property with every tenant_properties junction row.
              // Dedupe on property_id, mark the row matching the
              // person's primary property_id as Primary.
              const primaryPropId = tenant.property_id;
              // Merge all linked_properties rows for the same property into one.
              // When a tenant has both a "primary" row (no unit details) and a
              // junction row (with unit_id/unit_number) for the same property,
              // we want the unit info to win — not the first row encountered.
              const byPid = new Map<string, any>();
              for (const lp of (tenant.linked_properties || [])) {
                const pid = String(lp.property_id);
                const prev = byPid.get(pid) ?? {};
                byPid.set(pid, {
                  ...prev,
                  ...Object.fromEntries(Object.entries(lp).filter(([, v]) => v != null && v !== '')),
                  property_id: lp.property_id,
                });
              }
              const merged: any[] = Array.from(byPid.values()).map((lp) => ({
                ...lp,
                _primary: String(lp.property_id) === String(primaryPropId),
                role: lp.role || tenant.person_type || 'tenant',
              }));
              if (primaryPropId && !byPid.has(String(primaryPropId))) {
                merged.unshift({
                  property_id: primaryPropId,
                  role: tenant.person_type || 'tenant',
                  status: 'active',
                  _primary: true,
                });
              }
              const tpList = merged;
              return tpList.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">No property memberships found.</div>
              ) : (
              <Table
                columns={[
                  {
                    key: 'property_name',
                    header: 'Property',
                    render: (tp: any) => {
                      const prop = allProperties.find((p: any) => p.id === tp.property_id);
                      return (
                        <div>
                          <span className="font-medium text-gray-900">{prop?.name || `Property ${tp.property_id}`}</span>
                          {tp._primary && <span className="ml-2 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Primary</span>}
                        </div>
                      );
                    },
                  },
                  {
                    key: 'unit',
                    header: 'Unit',
                    render: (tp: any) => {
                      // Prefer the explicit unit_number string returned by the API.
                      // Fall back to a lookup against allUnits when the row only
                      // carries unit_id — fixes the case where the join row HAS
                      // a unit but its denormalized number wasn't populated.
                      const fromLookup = tp.unit_id ? allUnits.find((u: any) => u.id === tp.unit_id) : null;
                      const num = tp.unit_number || fromLookup?.unit_number;
                      return num
                        ? <span className="font-medium text-gray-900">#{num}</span>
                        : <span className="text-xs text-orange-600">No unit assigned</span>;
                    },
                  },
                  {
                    key: 'role',
                    header: 'Role',
                    render: (tp: any) => tp.role ? <Badge color="blue">{tp.role}</Badge> : <span className="text-gray-400">—</span>,
                  },
                  {
                    key: 'online_status',
                    header: 'Online Status',
                    render: (tp: any) => {
                      const s = tp.online_status || tp.connection_status;
                      return s ? <Badge color={s === 'online' ? 'green' : 'gray'}>{s}</Badge> : <span className="text-gray-400">—</span>;
                    },
                  },
                  {
                    key: 'access_status',
                    header: 'Access Status',
                    render: (tp: any) => {
                      const s = tp.access_status || tp.status;
                      return s ? <Badge color={s === 'active' ? 'green' : 'gray'}>{s}</Badge> : <span className="text-gray-400">—</span>;
                    },
                  },
                ]}
                data={tpList}
              />
            );
            })()}
          </Card>

          {/* Guests Created */}
          <Card id="person-guests" className={detailTab === 'guests' ? '' : 'hidden'} padding={false}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">Guests Created by This Person ({guestsCreated.length})</h3>
            </div>
            {guestsCreated.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No guests created by this person.</div>
            ) : (
              <Table
                columns={[
                  {
                    key: 'guest_name',
                    header: 'Guest Name',
                    render: (g: any) => (
                      <span className="font-medium text-gray-900">
                        {[g.guest_first_name, g.guest_last_name].filter(Boolean).join(' ') || g.guest_name || '—'}
                      </span>
                    ),
                  },
                  {
                    key: 'property',
                    header: 'Property',
                    render: (g: any) => {
                      const prop = allProperties.find((p: any) => p.id === g.property_id);
                      return <span className="text-sm text-gray-600">{prop?.name || g.property_name || (g.property_id ? `Property ${g.property_id}` : 'Unassigned')}</span>;
                    },
                  },
                  {
                    key: 'pin',
                    header: 'PIN',
                    render: (g: any) => (g.pin || g.pin_code)
                      ? <span className="font-mono text-sm font-bold text-brand-700 bg-brand-50 px-2 py-0.5 rounded">{g.pin || g.pin_code}</span>
                      : <span className="text-gray-400">—</span>,
                  },
                  {
                    key: 'expires_at',
                    header: 'Expiry',
                    render: (g: any) => g.expires_at
                      ? <span className="text-sm text-gray-600">{formatDateTime(g.expires_at)}</span>
                      : <span className="text-xs text-gray-400 italic">No Expiry</span>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (g: any) => {
                      const statusColors: Record<string, string> = { active: 'green', expired: 'gray', revoked: 'red' };
                      const s = g.status || 'active';
                      return <Badge color={statusColors[s] || 'gray'}>{s}</Badge>;
                    },
                  },
                ]}
                data={guestsCreated}
              />
            )}
          </Card>

          <Card id="person-deliveries" className={detailTab === 'guests' ? '' : 'hidden'} padding={false}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">Deliveries Created by This Person ({deliveriesCreated.length})</h3>
            </div>
            {deliveriesCreated.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No delivery codes created by this person.</div>
            ) : (
              <Table
                columns={[
                  {
                    key: 'delivery',
                    header: 'Delivery',
                    render: (delivery: any) => (
                      <div>
                        <span className="font-medium text-gray-900">{delivery.delivery_carrier || delivery.carrier || delivery.label || 'Delivery'}</span>
                        {(delivery.delivery_notes || delivery.notes) && <span className="block text-xs text-gray-500">{delivery.delivery_notes || delivery.notes}</span>}
                      </div>
                    ),
                  },
                  {
                    key: 'property',
                    header: 'Property',
                    render: (delivery: any) => {
                      const property = allProperties.find((item: any) => String(item.id) === String(delivery.property_id));
                      return <span className="text-sm text-gray-600">{property?.name || delivery.property_name || 'Unassigned'}</span>;
                    },
                  },
                  {
                    key: 'pin',
                    header: 'PIN',
                    render: (delivery: any) => (delivery.pin || delivery.pin_code)
                      ? <span className="font-mono text-sm font-bold text-brand-700 bg-brand-50 px-2 py-0.5 rounded">{delivery.pin || delivery.pin_code}</span>
                      : <span className="text-gray-400">—</span>,
                  },
                  {
                    key: 'arrival',
                    header: 'Expected Arrival',
                    render: (delivery: any) => {
                      const value = delivery.delivery_arrival_at || delivery.arrival_at || delivery.expected_arrival_at;
                      return value ? formatDateTime(value) : <span className="text-gray-400">—</span>;
                    },
                  },
                  {
                    key: 'expires',
                    header: 'Expiry',
                    render: (delivery: any) => delivery.expires_at || delivery.valid_until
                      ? formatDateTime(delivery.expires_at || delivery.valid_until)
                      : <span className="text-gray-400">—</span>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (delivery: any) => {
                      const status = delivery.revoked || delivery.is_revoked
                        ? 'revoked'
                        : delivery.completed_at || delivery.delivered_at || delivery.used_at
                          ? 'completed'
                          : delivery.status || 'active';
                      return <Badge color={status === 'active' || status === 'outstanding' ? 'green' : status === 'revoked' ? 'red' : 'gray'}>{status}</Badge>;
                    },
                  },
                ]}
                data={deliveriesCreated}
              />
            )}
          </Card>

          {/* Doors with access — every door the tenant can unlock via PIN /
              face / mobile app, grouped by property. Mobile Access toggle
              per row reflects this tenant's effective mobile-app
              visibility for that door (baseline ∪ grants \ hides). */}
          {accessibleDoors.length > 0 && (
            <Card id="person-access" className={detailTab === 'access' ? '' : 'hidden'} padding={false}>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Devices with Access ({accessibleDoors.length})</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Every access device available to this person. Toggle mobile access, then save the changes.</p>
                </div>
                <div className="flex items-center gap-2">
                  {dirtyPropertyIds.length > 0 && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                      Unsaved changes in {dirtyPropertyIds.length} {dirtyPropertyIds.length === 1 ? 'property' : 'properties'}
                    </span>
                  )}
                  <Button
                    size="sm"
                    onClick={saveDoorAccessChanges}
                    disabled={dirtyPropertyIds.length === 0 || doorAccessSaving}
                  >
                    {doorAccessSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
              <div className="person-access-device-list">{accessibleDoors.map((d:any) => { const propId=String(d.property_id); const state=doorAccessByProp[propId]; const key=String(d.id||d.uuid); const effective=((state?.baseline.has(key)??false)||(state?.grants.has(key)??false))&&!(state?.hides.has(key)??false); const p=allProperties.find((x:any)=>String(x.id)===propId); const type=String(d.type||d.device_type||'reader').toLowerCase(); const icon=type.includes('camera')?'▣':type.includes('hub')?'◆':type.includes('sensor')?'◉':'▥'; return <article className="person-access-device" key={key}><div className="person-device-icon" aria-hidden="true">{icon}</div><div className="person-device-copy"><h4>{d.name||d.device_name||`Device ${key.slice(0,6)}`}</h4><p>{p?.name||'Unassigned property'} · <span>{type.replace(/_/g,' ')}</span></p><small className={String(d.status).toLowerCase()==='online'?'online':''}>{d.status||'Unknown'}</small></div><div className="person-device-toggle"><span>Mobile access</span><button type="button" disabled={doorAccessSaving} onClick={()=>toggleTenantDoorAccess(propId,key)} className={`person-switch ${effective?'on':''}`} role="switch" aria-checked={effective}><i /></button></div></article>; })}</div>
              {/* Legacy table retained in source for rollback, never rendered. */}<div className="hidden"><Table
                columns={[
                  {
                    key: 'name',
                    header: 'Door',
                    render: (d: any) => (
                      <span className="font-medium text-gray-900">
                        {d.name || d.device_name || `Door ${String(d.id).slice(0, 6)}`}
                      </span>
                    ),
                  },
                  {
                    key: 'property',
                    header: 'Property',
                    render: (d: any) => {
                      const p = allProperties.find((x: any) => x.id === d.property_id);
                      return p ? (
                        <button
                          onClick={() => navigate(`/properties/${p.uuid || p.id}`)}
                          className="text-brand-600 hover:underline text-left"
                        >{p.name}</button>
                      ) : <span className="text-gray-400">—</span>;
                    },
                  },
                  {
                    key: 'type',
                    header: 'Type',
                    render: (d: any) => <span className="text-xs text-gray-500">{formatDeviceType(d.type || d.device_type, 'Door')}</span>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (d: any) => {
                      const s = String(d.status || 'unknown').toLowerCase();
                      const color = s === 'online' ? 'green' : s === 'offline' ? 'red' : 'gray';
                      return <Badge color={color}>{s}</Badge>;
                    },
                  },
                  {
                    key: 'mobile_access',
                    header: 'Mobile Access',
                    render: (d: any) => {
                      const propId = String(d.property_id);
                      const state = doorAccessByProp[propId];
                      const key = String(d.id);
                      const inBaseline = state?.baseline.has(key) ?? false;
                      const granted = state?.grants.has(key) ?? false;
                      const hidden = state?.hides.has(key) ?? false;
                      const effective = (inBaseline || granted) && !hidden;
                      const isSaving = doorAccessSaving;
                      const overrideTag = granted
                        ? <span className="block text-[10px] text-brand-600 font-medium mt-0.5">granted</span>
                        : hidden
                          ? <span className="block text-[10px] text-red-600 font-medium mt-0.5">hidden</span>
                          : null;
                      return (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isSaving}
                            onClick={() => toggleTenantDoorAccess(propId, key)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 ${effective ? 'bg-green-500' : 'bg-gray-300'} ${isSaving ? 'opacity-50' : ''}`}
                            role="switch"
                            aria-checked={effective}
                            aria-label={`Mobile access for ${d.name ?? 'door'}`}
                          >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${effective ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                          {overrideTag && <div>{overrideTag}</div>}
                        </div>
                      );
                    },
                  },
                ]}
                data={accessibleDoors.map((d) => ({ ...d, id: d.id || d.uuid }))}
              /></div>
            </Card>
          )}

          {/* Call Schedules — tenant-defined windows that govern when
              voice / video calls ring the iOS app. Synced from the iOS
              Settings → Call Schedule page. Property column lights up so
              admins can see which buildings each rule applies to. */}
          <Card id="person-schedules" className={detailTab === 'schedules' ? '' : 'hidden'}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">Call Schedules</h3>
              <span className="text-xs text-gray-500">{callSchedulesUnavailable ? '— rules verified' : `${callSchedules.length} rule${callSchedules.length === 1 ? '' : 's'}`}</span>
            </div>
            {callSchedulesUnavailable && <p role="alert" className="mb-3 border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">Synced call schedules could not be loaded. Mobile devices may have local schedules that are not shown here.</p>}
            {callSchedules.length === 0 && (
              <p className="text-sm text-gray-500 mb-3">{callSchedulesUnavailable ? 'No verified synced schedules are available.' : 'No synced schedules are set for this person.'}</p>
            )}
            {(
              <Table
                columns={[
                  { key: 'name', header: 'Name', render: (s: any) => s.name || <span className="text-gray-400">Untitled</span> },
                  {
                    key: 'days', header: 'Days',
                    render: (s: any) => {
                      const labels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                      if (!Array.isArray(s.days) || s.days.length === 0) return 'Daily';
                      return (s.days as number[]).slice().sort().map((d) => labels[d]).join(', ');
                    },
                  },
                  {
                    key: 'window', header: 'Window',
                    render: (s: any) => {
                      const fmt = (hhmm: string) => {
                        const [hStr, mStr] = (hhmm || '').split(':');
                        const h24 = Number(hStr) || 0;
                        const m = Number(mStr) || 0;
                        const ampm = h24 >= 12 ? 'PM' : 'AM';
                        const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
                        return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                      };
                      return `${fmt(s.start_time)} – ${fmt(s.end_time)}`;
                    },
                  },
                  {
                    key: 'mode', header: 'Mode',
                    render: (s: any) => (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.accept_during_window ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {s.accept_during_window ? 'Ring' : 'Block'}
                      </span>
                    ),
                  },
                  {
                    key: 'types', header: 'Types',
                    render: (s: any) => {
                      const parts: string[] = [];
                      if (s.allow_voice) parts.push('Voice');
                      if (s.allow_video) parts.push('Video');
                      return parts.length ? parts.join(' + ') : '—';
                    },
                  },
                  {
                    key: 'properties', header: 'Properties',
                    render: (s: any) => {
                      const ids = Array.isArray(s.property_ids) ? s.property_ids : [];
                      if (ids.length === 0) return <span className="text-gray-500">All properties</span>;
                      const names = ids.map((pid: string) => {
                        const p = allProperties.find((x: any) => x.id === pid);
                        return p ? p.name : pid.slice(0, 8);
                      });
                      return names.join(', ');
                    },
                  },
                ]}
                data={callSchedules.map((s: any, i: number) => ({ ...s, id: s.id || `cs-${i}` }))}
              />
            )}
          </Card>

          {/* Call History — embeds the global CallHistory page scoped
              to this tenant so the layout, filters and rendering match
              every other call history view exactly. */}
          {id && detailTab === 'calls' && <div id="person-calls"><CallHistory presetTenantId={id} hidePageHeader /></div>}
        </div>
      </div>
      <Modal open={editModal} onClose={() => setEditModal(false)} title="Edit Person">
        <TenantForm initialData={tenant} onSubmit={handleUpdate} onCancel={() => setEditModal(false)} />
      </Modal>

      <Modal open={addPropertyModal} onClose={() => { setAddPropertyModal(false); setLastAddResult(null); }} title="Add Property">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Choose a property and a specific unit. The activation code is only issued once a unit is assigned so calls route to a real address.</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Property</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={addPropertySelected}
              onChange={(e) => setAddPropertySelected(e.target.value)}
            >
              <option value="">Select a property…</option>
              {allProperties
                .filter((p: any) => !(tenant?.linked_properties || []).some((lp: any) => lp.property_id === p.id))
                .map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name} — {p.address}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={addUnitSelected}
              onChange={(e) => setAddUnitSelected(e.target.value)}
              disabled={!addPropertySelected || unitsForSelectedProperty.length === 0}
            >
              <option value="">{addPropertySelected
                ? (unitsForSelectedProperty.length === 0 ? 'No units in this property' : 'Select a unit…')
                : 'Pick a property first'}</option>
              {unitsForSelectedProperty.map((u: any) => (
                <option key={u.id} value={u.id}>Unit #{u.unit_number}{u.floor ? ` · Floor ${u.floor}` : ''}</option>
              ))}
            </select>
          </div>
          {lastAddResult?.code && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-700">Activation code generated:</p>
              <p className="font-mono text-2xl font-bold text-green-900 mt-1">{lastAddResult.code}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setAddPropertyModal(false); setLastAddResult(null); }}>Close</Button>
            <Button onClick={handleAddProperty} disabled={!addPropertySelected || !addUnitSelected || addingProperty}>
              {addingProperty ? 'Adding…' : 'Add Property'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={addCustomerModal} onClose={() => setAddCustomerModal(false)} title="Assign Customer">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">A person can belong to multiple customers without duplicate records.</p>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={addCustomerSelected}
            onChange={(e) => setAddCustomerSelected(e.target.value)}
          >
            <option value="">Select a customer…</option>
            {allCustomers
              .filter((c: any) => !(tenant?.linked_customers || []).some((lc: any) => lc.customer_id === c.id))
              .map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </select>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddCustomerModal(false)}>Cancel</Button>
            <Button onClick={handleAddCustomer} disabled={!addCustomerSelected || addingCustomer}>
              {addingCustomer ? 'Assigning…' : 'Assign'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
